import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';

// Generate initials with SS rule
const generateInitials = (firstName, lastName) => {
  if (!firstName || !lastName) return '';

  const firstInitial = firstName.charAt(0).toUpperCase();
  const lastInitial = lastName.charAt(0).toUpperCase();

  // Special case: If both initials are S, use first 2 letters of first name
  if (firstInitial === 'S' && lastInitial === 'S') {
    return firstName.substring(0, 2).charAt(0).toUpperCase() +
           firstName.substring(1, 2).toLowerCase();
  }

  return firstInitial + lastInitial;
};

/**
 * Staff Member Modal - Quick staff selection after order completion
 *
 * Features:
 * - Shows current staff member with quick confirm (Enter key)
 * - Keyboard shortcuts (1, 2, 3, etc.) to switch staff
 * - Can be dismissed with Escape
 * - Reusable for order creation, editing, and other contexts
 *
 * @param {boolean} isOpen - Whether modal is visible
 * @param {function} onClose - Called when modal is dismissed
 * @param {function} onSelect - Called when staff member is selected (receives staff member object)
 * @param {string} title - Modal title (default: "Creating order as")
 * @param {string} subtitle - Modal subtitle/context
 */
const StaffMemberModal = ({
  isOpen,
  onClose,
  onSelect,
  title = "Creating order as",
  subtitle = null
}) => {
  const { user } = useAuth();
  const [selectedStaffMember, setSelectedStaffMember] = useState(null);
  const modalRef = useRef(null);

  // Check if user is staff account
  const isStaffAccount = user && user.accountType === 'staff';
  const staffMembers = (user?.staffMembers || []).filter(m => m.active);

  // Load currently selected staff member from localStorage
  useEffect(() => {
    if (isStaffAccount) {
      const saved = localStorage.getItem('selectedStaffMember');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          // Verify the staff member still exists in the current list
          const exists = staffMembers.find(m => m.id === parsed.id);
          if (exists) {
            setSelectedStaffMember(parsed);
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
    }
  }, [isStaffAccount, isOpen]);

  // Keyboard shortcuts handler
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      // Escape to close
      if (e.key === 'Escape') {
        handleClose();
        return;
      }

      // Enter to confirm current staff member
      if (e.key === 'Enter') {
        if (selectedStaffMember) {
          handleSelectStaffMember(selectedStaffMember);
        }
        return;
      }

      // Number keys 1-9 to select staff member
      const num = parseInt(e.key);
      if (!isNaN(num) && num >= 1 && num <= staffMembers.length) {
        const member = staffMembers[num - 1];
        handleSelectStaffMember(member);
        return;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedStaffMember, staffMembers]);

  // Focus modal when opened
  useEffect(() => {
    if (isOpen && modalRef.current) {
      modalRef.current.focus();
    }
  }, [isOpen]);

  const handleSelectStaffMember = (member) => {
    const memberWithInitials = {
      ...member,
      initials: member.initials || generateInitials(member.firstName, member.lastName)
    };

    // Save to localStorage
    setSelectedStaffMember(memberWithInitials);
    localStorage.setItem('selectedStaffMember', JSON.stringify(memberWithInitials));

    // Trigger storage event for other components (like Sidebar)
    window.dispatchEvent(new Event('storage'));

    // Call parent callback
    if (onSelect) {
      onSelect(memberWithInitials);
    }

    // Close modal
    handleClose();
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  // Don't render if not a staff account or no staff members
  if (!isStaffAccount || staffMembers.length === 0 || !isOpen) {
    return null;
  }

  const currentStaffMember = selectedStaffMember || staffMembers[0];

  return (
    <div className="fixed inset-0 bg-[color:var(--c-backdrop)] flex items-center justify-center z-50">
      {/* Dialog pattern: 16px radius, dialog shadow, no tinted header. */}
      <div
        ref={modalRef}
        tabIndex={-1}
        className="bg-white border border-gray-200 rounded-2xl shadow-lg max-w-2xl w-full mx-4 overflow-hidden"
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <h3 className="text-[17px] leading-6 font-semibold tracking-[-0.01em] text-gray-900">{title}</h3>
          {subtitle && (
            <p className="text-[13px] text-gray-600 mt-1">{subtitle}</p>
          )}
        </div>

        {/* Current Staff Member - Quick Confirm */}
        <div className="px-6 py-4 border-y border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center text-white font-medium text-lg"
                style={{ backgroundColor: currentStaffMember.avatarColor || '#10b981' }}
              >
                {currentStaffMember.initials || generateInitials(currentStaffMember.firstName, currentStaffMember.lastName)}
              </div>
              <div>
                <p className="font-medium text-gray-900">
                  {currentStaffMember.firstName} {currentStaffMember.lastName}
                </p>
                <p className="text-xs text-gray-500">Press Enter to confirm</p>
              </div>
            </div>
            <button
              onClick={() => handleSelectStaffMember(currentStaffMember)}
              className="btn-primary"
            >
              Confirm
            </button>
          </div>
        </div>

        {/* Other Staff Members */}
        {staffMembers.length > 1 && (
          <div className="px-6 py-4 max-h-96 overflow-y-auto">
            <p className="text-[13px] font-medium text-gray-900 mb-3">
              Or select a different staff member:
            </p>
            <div className="grid grid-cols-2 gap-2">
              {staffMembers.map((member, index) => {
                const isCurrentMember = currentStaffMember.id === member.id;
                if (isCurrentMember) return null; // Skip current member since it's shown above

                const initials = member.initials || generateInitials(member.firstName, member.lastName);
                const shortcutKey = staffMembers.findIndex(m => m.id === member.id) + 1;

                return (
                  <button
                    key={member.id}
                    onClick={() => handleSelectStaffMember(member)}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-md border border-gray-200 hover:border-gray-300 hover:bg-floral-50 transition-colors"
                  >
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white font-medium text-sm"
                      style={{ backgroundColor: member.avatarColor || '#10b981' }}
                    >
                      {initials}
                    </div>
                    <div className="text-left flex-1 min-w-0">
                      <p className="font-medium text-gray-900 text-sm truncate">
                        {member.firstName} {member.lastName}
                      </p>
                      <p className="text-xs text-gray-500">Press {shortcutKey}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Footer - Keyboard hints */}
        <div className="border-t border-gray-200 px-6 py-3">
          <div className="flex items-center justify-between text-xs text-gray-600">
            <div className="flex items-center gap-4">
              <span>
                <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-xs font-mono text-gray-900">Enter</kbd> Confirm
              </span>
              {staffMembers.length > 1 && (
                <span>
                  <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-xs font-mono text-gray-900">1-{staffMembers.length}</kbd> Select
                </span>
              )}
            </div>
            <button
              onClick={handleClose}
              className="text-gray-600 hover:text-gray-900 transition-colors"
            >
              <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-xs font-mono text-gray-900">Esc</kbd> Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffMemberModal;
